/**
 * Locale Context
 * Manages user locale state and provides methods to update it
 */
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';

interface LocaleContextType {
  locale: string;
  setLocale: (locale: string) => void;
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

export const useLocale = () => {
  const context = useContext(LocaleContext);
  if (context === undefined) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context;
};

interface LocaleProviderProps {
  children: ReactNode;
}

export const LocaleProvider: React.FC<LocaleProviderProps> = ({ children }) => {
  // Initialize locale from localStorage or default to 'en-US'
  const getInitialLocale = (): string => {
    if (typeof window !== 'undefined') {
      const storedLocale = localStorage.getItem('locale');
      return storedLocale || 'en-US';
    }
    return 'en-US';
  };

  const [locale, setLocale] = useState<string>(getInitialLocale);

  // Update localStorage whenever locale changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('locale', locale);
    }
  }, [locale]);

  // Wrapper function to update locale and localStorage
  const updateLocale = (newLocale: string) => {
    setLocale(newLocale);
    if (typeof window !== 'undefined') {
      localStorage.setItem('locale', newLocale);
    }
  };

  const value: LocaleContextType = {
    locale,
    setLocale: updateLocale,
  };

  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
};
